import EntryActions from 'actions/EntryActions';
import {assert} from 'Asserts';
import {areDataModsEnabledAnywhere, DataModificationsHeader} from 'audit/DataModifications';
import {pluralize} from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as context from 'Context';
import DocumentActions from 'notebook/documents/DocumentActions';
import {waitForEntryOrTemplateSaved} from 'notebook/entries/BaseEntryUtil';
import {
  performServerSideWriteActionWithAudits,
  ActionResultType,
  ServerSideWriteActionResult,
  WAIT_FOR_ENTRY_SAVED_INTERVAL_MS,
  WAIT_FOR_ENTRY_SAVED_SECONDS,
} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import {invalidateDataModSummaryCacheForTable} from 'notebook/structured-tables/DataModificationSummaryQuery';
import notifications from 'notifications';
import APIUtil from 'util/APIUtil';
import * as StageEntryActions from 'workflows/stage-entries/StageEntryActions';

import {UnknownStructuredTable} from '../StructuredTableConstants';
import {hasAnyFinalizedRows, hasAnyFinalizedRowsInRanges} from './hasAnyFinalizedRows';

export const _forTesting = {
  _performTableOpWithDataMods,
};

/**
 * Performs a table operation with data modifications.
 *
 * The flow is:
 * 1. Check table-op specific short-circuit condition to skip data mods logic.
 * 2. Show data mods modal depending on tenant configs
 * 3. Save the document to flush other previous unrelated changes to the document
 * 4. Perform the table operation
 * 5. Force a document save with just the table op changes and the dataModsHeader sent to the save endpoint
 */
async function _performTableOpWithDataMods({
  entryId,
  changeSummary,
  loadingScreenMessage,
  autoSaverAction,
  tableApiId,
  shouldSkipDataMods,
  performTableOp,
}: {
  entryId: string;
  changeSummary: string;
  loadingScreenMessage: string;
  autoSaverAction: string;
  tableApiId: string | undefined;
  shouldSkipDataMods: () => boolean;
  performTableOp: () => void;
}): Promise<void> {
  if (!areDataModsEnabledAnywhere() || shouldSkipDataMods()) {
    performTableOp();
    return;
  }

  const saveDocument = async (
    dataModsHeader?: DataModificationsHeader
  ): Promise<ServerSideWriteActionResult<void>> => {
    assert(APIUtil.isExecutionDocument(entryId), 'Unexpected entry type', {entryId});
    const documentType = APIUtil.apiIdToType(entryId);

    // Revalidate first so the document can accept state changes (Invalidated as part of performServerSideWriteAction)
    if (documentType === ModelTypeConstants.STAGE_ENTRY) {
      StageEntryActions.revalidate(entryId);
    } else if (documentType === ModelTypeConstants.DOCUMENT) {
      DocumentActions.revalidate(entryId);
    } else {
      EntryActions.revalidate(entryId);
    }

    // Perform the table operation while document is valid
    performTableOp();

    // Save the document with the table op changes and the dataModsHeader sent to the save endpoint
    // Force an immediate save on the locked document
    await context.usingAsync({immediateAutoSaverQueueSaveAction: autoSaverAction}, null, async () => {
      if (documentType === ModelTypeConstants.STAGE_ENTRY) {
        await StageEntryActions.save(entryId, dataModsHeader);
      } else if (documentType === ModelTypeConstants.DOCUMENT) {
        await DocumentActions.save(entryId, dataModsHeader);
      } else {
        await EntryActions.save(entryId, dataModsHeader);
      }
    });

    // Wait for save to complete
    const saved = await waitForEntryOrTemplateSaved(
      entryId,
      WAIT_FOR_ENTRY_SAVED_INTERVAL_MS,
      WAIT_FOR_ENTRY_SAVED_SECONDS
    );

    if (tableApiId) {
      invalidateDataModSummaryCacheForTable(tableApiId);
    }

    if (!saved) {
      notifications.error('Table operation requires the entry to be saved.');
      return {resultType: ActionResultType.ABORTED};
    }

    return {resultType: ActionResultType.SUCCESS, responseData: undefined};
  };

  await performServerSideWriteActionWithAudits({
    entryId,
    changeSummary,
    loadingScreenMessage,
    autoSaverAction,
    doPerformAction: (
      _tableSubmissionDiffs,
      _updateStatus,
      dataModsHeader?: DataModificationsHeader
    ): Promise<ServerSideWriteActionResult<void>> => saveDocument(dataModsHeader),
    areFeatureDataModsEnabled: true,
  });
}

/**
 * Wraps a row deletion operation with data modifications modal if finalized rows are affected.
 */
export function performRowDeletionWithDataMods({
  entryId,
  structuredTable,
  start,
  end,
  onPerformDeletion,
}: {
  entryId: string;
  structuredTable: UnknownStructuredTable;
  start: number;
  end: number;
  onPerformDeletion: () => void;
}): Promise<void> {
  const numRows = end - start + 1;
  const changeSummary = `${numRows} ${pluralize(numRows, 'row')} will be deleted.`;

  const shouldSkipDataMods = (): boolean => !hasAnyFinalizedRows(structuredTable, start, end);

  return _performTableOpWithDataMods({
    entryId,
    changeSummary,
    loadingScreenMessage: 'Deleting rows...',
    autoSaverAction: 'row_deletion',
    tableApiId: structuredTable.apiId,
    shouldSkipDataMods,
    performTableOp: onPerformDeletion,
  });
}

/**
 * Wraps a non-contiguous row deletion operation with data modifications modal if finalized rows are affected.
 */
export function performNonContiguousRowDeletionWithDataMods({
  entryId,
  structuredTable,
  ranges,
  onPerformDeletion,
}: {
  entryId: string;
  structuredTable: UnknownStructuredTable;
  ranges: Array<[number, number]>;
  onPerformDeletion: () => void;
}): Promise<void> | void {
  const numRows = ranges.reduce((sum, [start, end]) => sum + (end - start + 1), 0);
  const changeSummary = `${numRows} ${pluralize(numRows, 'row')} will be deleted.`;

  const shouldSkipDataMods = (): boolean => !hasAnyFinalizedRowsInRanges(structuredTable, ranges);

  return _performTableOpWithDataMods({
    entryId,
    changeSummary,
    loadingScreenMessage: 'Deleting rows...',
    autoSaverAction: 'row_deletion',
    tableApiId: structuredTable.apiId,
    shouldSkipDataMods,
    performTableOp: onPerformDeletion,
  });
}
